import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Text } from './Typography.js'

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`

const TooltipContent = styled.div`
  position: fixed;
  background-color: white;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease;
  z-index: 1000;
  box-shadow: 0px 0px 1px 0px rgba(12, 26, 75, 0.24), 0px 3px 8px -1px rgba(50, 50, 71, 0.05);
`

const Tooltip = ({ children, label, direction = 'right', disabled = false }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)

  const updatePosition = () => {
    if (!containerRef.current || !tooltipRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()

    let top = 0
    let left = 0
    let transform = ''

    // Calculate initial position based on direction
    switch (direction) {
      case 'left':
        top = rect.top + rect.height / 2
        left = rect.left - 4
        transform = 'translate(-100%, -50%)'
        break
      case 'right':
        top = rect.top + rect.height / 2
        left = rect.right + 4
        transform = 'translate(0, -50%)'
        break
      case 'top':
        top = rect.top - 4
        left = rect.left + rect.width / 2
        transform = 'translate(-50%, -100%)'
        break
      case 'bottom':
        top = rect.bottom + 4
        left = rect.left + rect.width / 2
        transform = 'translate(-50%, 0)'
        break
      default:
        top = rect.top + rect.height / 2
        left = rect.right + 4
        transform = 'translate(0, -50%)'
    }

    // Check viewport boundaries and adjust position if needed
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Check horizontal boundaries
    if (left < tooltipRect.width / 2) {
      left = tooltipRect.width / 2
    } else if (left > viewportWidth - tooltipRect.width / 2) {
      left = viewportWidth - tooltipRect.width / 2
    }

    // Check vertical boundaries
    if (top < tooltipRect.height / 2) {
      top = tooltipRect.height / 2
    } else if (top > viewportHeight - tooltipRect.height / 2) {
      top = viewportHeight - tooltipRect.height / 2
    }

    setPosition({ top, left, transform })
  }

  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure the tooltip is rendered and measured
      requestAnimationFrame(() => {
        updatePosition()
      })
      window.addEventListener('scroll', updatePosition)
      window.addEventListener('resize', updatePosition)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isVisible, direction])

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsVisible(true)
    }
  }

  const handleMouseLeave = () => {
    setIsVisible(false)
  }

  console.log('label', label)
  if (!label) {
    return children
  }

  return (
    <TooltipContainer ref={containerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      {!disabled &&
        createPortal(
          <TooltipContent
            ref={tooltipRef}
            className="tooltip"
            isVisible={isVisible}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: position.transform
            }}
          >
            <Text fontWeight="500" light>
              {label}
            </Text>
          </TooltipContent>,
          document.body
        )}
    </TooltipContainer>
  )
}

export default Tooltip
